/**
 * APEXION - Live Trading Dashboard
 * Monitor live positions, orders, and trading stats
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { toast } from 'sonner';
import api from '../utils/api';
import {
  ArrowLeft,
  TrendingUp,
  TrendingDown,
  Activity,
  DollarSign,
  AlertTriangle,
  RefreshCw,
  Power,
  XCircle,
  Clock,
  Zap,
} from 'lucide-react';

const LiveTradingDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [positions, setPositions] = useState([]);
  const [orders, setOrders] = useState([]);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 10000); // Refresh every 10s
    return () => clearInterval(interval);
  }, []);

  const loadData = async () => {
    try {
      const [statsRes, positionsRes, ordersRes] = await Promise.all([
        api.get('/trading/live/stats'),
        api.get('/trading/live/positions'),
        api.get('/trading/live/orders'),
      ]);
      
      setStats(statsRes.data);
      setPositions(positionsRes.data?.positions || []);
      setOrders(ordersRes.data?.orders || []);
    } catch (error) {
      console.error('Failed to load live trading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
    toast.success('Data refreshed');
  };

  const handleClosePosition = async (positionId) => {
    try {
      await api.post(`/trading/live/close-position/${positionId}`);
      toast.success('Close order queued');
      await loadData();
    } catch (error) {
      toast.error('Failed to close position');
    }
  };

  const handleEmergencyClose = async () => {
    if (!window.confirm('Are you sure you want to close ALL positions? This action cannot be undone.')) {
      return;
    }
    
    try {
      await api.post('/trading/live/emergency-close-all');
      toast.success('Emergency close initiated');
      await loadData();
    } catch (error) {
      toast.error('Failed to initiate emergency close');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0D12] text-[#F5F7FA] flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0D12] text-[#F5F7FA]">
      {/* Header */}
      <header className="glass-effect border-b border-[#1E293B] sticky top-0 z-50">
        <div className="container mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/dashboard')}
              className="text-[#94A3B8] hover:text-[#F5F7FA]"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-yellow-500" />
              <span className="text-xl font-bold font-['Outfit']">Live Trading</span>
            </div>
            <Badge className={stats?.status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}>
              {stats?.status === 'active' ? 'Engine Active' : 'Engine Inactive'}
            </Badge>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={refreshing}
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={handleEmergencyClose}
              disabled={positions.length === 0}
            >
              <Power className="w-4 h-4 mr-2" />
              Emergency Close All
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 md:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-[#15171C] border-[#1E293B]">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#94A3B8]">Total Trades</p>
                  <p className="text-2xl font-bold font-['JetBrains_Mono']">{stats?.total_trades || 0}</p>
                </div>
                <Activity className="w-8 h-8 text-[#2F6BFF]" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-[#15171C] border-[#1E293B]">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#94A3B8]">Realized P&L</p>
                  <p className={`text-2xl font-bold font-['JetBrains_Mono'] ${stats?.total_realized_pnl >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                    ${stats?.total_realized_pnl?.toFixed(2) || '0.00'}
                  </p>
                </div>
                <DollarSign className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-[#15171C] border-[#1E293B]">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#94A3B8]">Unrealized P&L</p>
                  <p className={`text-2xl font-bold font-['JetBrains_Mono'] ${stats?.unrealized_pnl >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                    ${stats?.unrealized_pnl?.toFixed(2) || '0.00'}
                  </p>
                </div>
                {stats?.unrealized_pnl >= 0 ? (
                  <TrendingUp className="w-8 h-8 text-green-500" />
                ) : (
                  <TrendingDown className="w-8 h-8 text-red-500" />
                )}
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-[#15171C] border-[#1E293B]">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#94A3B8]">Win Rate</p>
                  <p className="text-2xl font-bold font-['JetBrains_Mono']">{stats?.win_rate?.toFixed(1) || '0'}%</p>
                </div>
                <Activity className="w-8 h-8 text-[#C9A24D]" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Open Positions */}
        <Card className="bg-[#15171C] border-[#1E293B] mb-8">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-yellow-500" />
                Open Positions ({positions.length})
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            {positions.length === 0 ? (
              <p className="text-center text-[#94A3B8] py-8">No open positions</p>
            ) : (
              <div className="space-y-3">
                {positions.map((position) => (
                  <div
                    key={position.id}
                    className="bg-[#0B0D12] rounded-lg p-4 flex items-center justify-between"
                  >
                    <div className="flex items-center gap-4">
                      <Badge className={position.side === 'buy' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}>
                        {position.side.toUpperCase()}
                      </Badge>
                      <div>
                        <p className="font-semibold">{position.symbol}</p>
                        <p className="text-sm text-[#94A3B8]">
                          {position.quantity.toFixed(6)} @ ${position.entry_price.toFixed(2)}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-6">
                      <div className="text-right">
                        <p className="text-sm text-[#94A3B8]">Current Price</p>
                        <p className="font-['JetBrains_Mono']">${position.current_price?.toFixed(2) || '—'}</p>
                      </div>
                      
                      <div className="text-right">
                        <p className="text-sm text-[#94A3B8]">Unrealized P&L</p>
                        <p className={`font-['JetBrains_Mono'] ${position.unrealized_pnl >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                          ${position.unrealized_pnl?.toFixed(2) || '0.00'}
                        </p>
                      </div>
                      
                      {position.stop_loss_price && (
                        <div className="text-right">
                          <p className="text-sm text-[#94A3B8]">Stop Loss</p>
                          <p className="font-['JetBrains_Mono'] text-red-400">${position.stop_loss_price.toFixed(2)}</p>
                        </div>
                      )}
                      
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleClosePosition(position.id)}
                        className="text-red-400 border-red-400/50 hover:bg-red-400/10"
                      >
                        <XCircle className="w-4 h-4 mr-1" />
                        Close
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pending Orders */}
        <Card className="bg-[#15171C] border-[#1E293B]">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-[#94A3B8]" />
              Pending Orders ({orders.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {orders.length === 0 ? (
              <p className="text-center text-[#94A3B8] py-8">No pending orders</p>
            ) : (
              <div className="space-y-3">
                {orders.map((order) => (
                  <div
                    key={order.id}
                    className="bg-[#0B0D12] rounded-lg p-4 flex items-center justify-between"
                  >
                    <div className="flex items-center gap-4">
                      <Badge className={order.side === 'buy' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}>
                        {order.side.toUpperCase()}
                      </Badge>
                      <div>
                        <p className="font-semibold">{order.symbol}</p>
                        <p className="text-sm text-[#94A3B8]">
                          {order.quantity.toFixed(6)} {order.order_type}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <Badge variant="outline">{order.status}</Badge>
                      <p className="text-sm text-[#94A3B8]">
                        {new Date(order.created_at).toLocaleTimeString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Warning */}
        <Alert className="mt-8 bg-yellow-500/10 border-yellow-500/30">
          <AlertTriangle className="w-4 h-4 text-yellow-500" />
          <AlertDescription className="text-yellow-500">
            Live trading involves real money. Monitor your positions carefully and use stop losses to manage risk.
            Past performance does not guarantee future results.
          </AlertDescription>
        </Alert>
      </main>
    </div>
  );
};

export default LiveTradingDashboard;
